import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { AuthService } from '../../auth/auth.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { DefaultSocketService } from '../../common/websocket';
import { LoggerService } from '../../logger';

import { ChatMessage } from './model/chat-message.model';
import { ChatRequest } from './model/chat-request.model';
import { Chat } from './model/chat.model';
import { HubMethods } from './model/hub-methods.model';
import { ImportTranscriptParams } from './model/import-transcript-params.model';
import { ListMeetingParams } from './model/list-meeting-params.model';
import { MeetingRequest } from './model/meeting-request.model';
import { TaskItem } from './model/task-item.model';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class SocketService extends DefaultSocketService implements OnDestroy {
    readonly message: Observable<{ hubMethod: string; payload: any }>;
    private readonly _messageSubj: Subject<{ hubMethod: string; payload: any }>;

    constructor(
        private readonly _authService2: AuthService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _logger: LoggerService,
    ) {
        super(_authService2);

        this._messageSubj = new Subject();
        this.message = this._messageSubj.asObservable();

        this._setupBindings();
    }

    ngOnDestroy(): void {
        this._messageSubj?.complete();

        this._destroy();
    }

    async retrieveCurrentCall(): Promise<void> {
        await this.send(HubMethods.RetrieveCurrentCall);
    }

    async createMeeting(request: MeetingRequest): Promise<void> {
        await this.send(HubMethods.CreateMeeting, request);
    }

    async listMeetings(params: ListMeetingParams): Promise<void> {
        await this.send(HubMethods.ListMeetings, params);
    }

    async inviteToCurrentCall(inviteUserId: number): Promise<void> {
        await this.send(HubMethods.InviteToCurrentCall, inviteUserId);
    }

    async endCurrentCall(): Promise<void> {
        await this.send(HubMethods.EndCurrentCall);
    }

    async retrieveChat(request: ChatRequest): Promise<void> {
        await this.send(HubMethods.RetrieveChat, request);
    }

    async updateChat(chat: Chat): Promise<void> {
        await this.send(HubMethods.UpdateChat, chat);
    }

    async listChatMessages(chat: Chat): Promise<void> {
        await this.send(HubMethods.ListChatMessages, chat);
    }

    async sendChatMessage(chat: Chat, message: ChatMessage): Promise<void> {
        await this.send(HubMethods.SendChatMessage, chat, message);
    }

    async deleteChatMessage(chat: Chat, message: ChatMessage): Promise<void> {
        await this.send(HubMethods.DeleteChatMessage, chat, message);
    }

    async createTask(task: TaskItem): Promise<void> {
        await this.send(HubMethods.CreateTask, task);
    }

    async updateTask(task: TaskItem): Promise<void> {
        await this.send(HubMethods.UpdateTask, task);
    }

    async deleteTask(msftTaskId: string): Promise<void> {
        await this.send(HubMethods.DeleteTask, msftTaskId);
    }

    async listTasks(): Promise<void> {
        await this.send(HubMethods.ListTasks);
    }

    async importTranscript(params: ImportTranscriptParams): Promise<void> {
        await this.send(HubMethods.ImportTranscript, params);
    }

    protected _websocketUrl(): string {
        return `${this._appConfig.API_ENDPOINTS.TEAMS_CALL ?? ''}/ws`;
    }

    private _setupBindings(): void {
        if (this.created) {
            this._bind();
        }
        this.didCreate
            .pipe(
                tap((_socketService) => {
                    this._bind();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        if (this.connected) {
            void this.retrieveCurrentCall();
        }
        this.didConnect
            .pipe(
                tap((_socketService) => {
                    void this.retrieveCurrentCall();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bind(): void {
        const methods: HubMethods[] = [
            HubMethods.OnCurrentCall,
            HubMethods.OnMeetingList,
            HubMethods.OnCallStatus,
            HubMethods.OnChat,
            HubMethods.OnChatMessage,
            HubMethods.OnChatMessageList,
            HubMethods.OnTaskCreate,
            HubMethods.OnTaskList,
        ];

        methods.forEach((method) => {
            this.on(method, (payload) => {
                this._logger.debug({ method, payload });
                this._messageSubj.next({
                    hubMethod: method,
                    payload,
                });
            });
        });
    }
}
